import { useState } from 'react';
import { useTheme, themeColors } from '../ThemeContext';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';

const INDUSTRIES = ['AI & Automation', 'Robotics', 'FinTech', 'Cloud Computing', 'Cybersecurity', 'Blockchain', 'Healthcare', 'Sustainability', 'AR/VR', 'Data Analytics'];
const INTEREST_TAGS = ['Funding Opportunities', 'Partnership', 'Product Demo', 'Hiring', 'Networking', 'Investment', 'Technology', 'Research', 'B2B Sales', 'Innovation'];

const LANGUAGES = [
  { code: 'tr', flag: '🇹🇷', nameKey: 'lang.tr' },
  { code: 'de', flag: '🇩🇪', nameKey: 'lang.de' },
  { code: 'en', flag: '🇬🇧', nameKey: 'lang.en' },
];

const ProfileScreen = () => {
  const { theme, setTheme } = useTheme();
  const c = themeColors[theme];
  const isDark = theme === 'dark';
  const { t } = useTranslation();
  const [selectedIndustries, setSelectedIndustries] = useState(['AI & Automation', 'Robotics', 'FinTech']);
  const [selectedTags, setSelectedTags] = useState(['Partnership', 'Product Demo', 'Networking']);
  const [notifications, setNotifications] = useState({ aiSuggestions: true, boothUpdates: true, reminders: false, promotions: false });

  const toggleIndustry = (ind: string) => setSelectedIndustries(prev => prev.includes(ind) ? prev.filter(i => i !== ind) : [...prev, ind]);
  const toggleTag = (tag: string) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  const toggleNotif = (key: keyof typeof notifications) => setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  const changeLanguage = (code: string) => { i18n.changeLanguage(code); };

  const notifItems = [
    { key: 'aiSuggestions' as const, label: t('profile.notif.ai'), desc: t('profile.notif.ai.desc'), icon: 'ri-sparkling-line' },
    { key: 'boothUpdates' as const, label: t('profile.notif.booth'), desc: t('profile.notif.booth.desc'), icon: 'ri-building-2-line' },
    { key: 'reminders' as const, label: t('profile.notif.reminders'), desc: t('profile.notif.reminders.desc'), icon: 'ri-alarm-line' },
    { key: 'promotions' as const, label: t('profile.notif.promotions'), desc: t('profile.notif.promotions.desc'), icon: 'ri-gift-line' },
  ];

  const menuItems = [
    { icon: 'ri-download-cloud-line', label: t('profile.export'), sub: t('profile.export.desc') },
    { icon: 'ri-shield-check-line', label: t('profile.privacy'), sub: t('profile.privacy.desc') },
    { icon: 'ri-question-line', label: t('profile.help'), sub: t('profile.help.desc') },
    { icon: 'ri-information-line', label: t('profile.about'), sub: t('profile.about.desc') },
  ];

  return (
    <div className={`flex flex-col min-h-screen pb-24 ${c.bg}`}>
      {/* Header */}
      <div className={`${isDark ? 'bg-gradient-to-b from-[#0D1117] to-[#08090F]' : 'bg-gradient-to-b from-[#EEF2FF] to-[#F8F9FB]'} px-5 pt-12 pb-6`}>
        <div className="flex items-start justify-between mb-4">
          <h1 className={`text-xl font-black ${c.text}`}>{t('profile.title')}</h1>
          <button className={`text-xs font-semibold px-3 py-1.5 rounded-xl cursor-pointer whitespace-nowrap ${isDark ? 'bg-[#00d4ff]/10 text-[#00d4ff]' : 'bg-blue-50 text-blue-600'}`}>{t('profile.edit')}</button>
        </div>
        <div className={`flex items-center gap-4 p-4 rounded-2xl border ${c.bgCard} ${c.border} ${c.glow}`}>
          <div className="relative">
            <img src="https://readdy.ai/api/search-image?query=professional%20business%20headshot%20of%20young%20confident%20man%20with%20clean%20background%20and%20modern%20corporate%20look%20high%20quality%20portrait%20photography&width=120&height=120&seq=profile001&orientation=squarish" alt="Profile" className="w-16 h-16 rounded-2xl object-cover object-top" />
            <div className="absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-emerald-500 border-2 border-transparent">
              <i className="ri-check-line text-white text-[8px]"></i>
            </div>
          </div>
          <div className="flex-1">
            <h2 className={`text-base font-black ${c.text}`}>Alexander Chen</h2>
            <p className={`text-xs ${c.textMuted}`}>{t('profile.jobTitle')}</p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-[#00d4ff]/15 text-[#00d4ff]' : 'bg-blue-100 text-blue-700'}`}>
                <i className="ri-vip-crown-line mr-1"></i>{t('profile.proMember')}
              </span>
              <span className={`text-[10px] ${c.textMuted}`}>Dubai, UAE</span>
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 flex flex-col gap-5 pt-5">
        {/* Language Switch */}
        <div className={`p-4 rounded-2xl border ${c.bgCard} ${c.border}`}>
          <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>
            <i className="ri-translate-2 mr-1"></i>{t('profile.language')}
          </p>
          <div className="flex gap-2">
            {LANGUAGES.map(lang => {
              const isActive = i18n.language === lang.code;
              return (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    isActive
                      ? isDark ? 'bg-[#00d4ff]/15 border-[#00d4ff]/50 text-[#00d4ff]' : 'bg-blue-50 border-blue-300 text-[#0070E0]'
                      : `${c.bgSecondary} ${c.border} ${c.textMuted}`
                  }`}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <span className={`text-[10px] font-bold`}>{t(lang.nameKey)}</span>
                  {isActive && <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-[#00d4ff]' : 'bg-[#0070E0]'}`}></span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Theme Switch */}
        <div className={`p-4 rounded-2xl border ${c.bgCard} ${c.border}`}>
          <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>
            <i className="ri-palette-line mr-1"></i>{t('profile.theme')}
          </p>
          <div className={`flex gap-2 p-1 rounded-2xl ${isDark ? 'bg-[#0D1117]' : 'bg-gray-100'}`}>
            <button onClick={() => setTheme('dark')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap ${isDark ? 'bg-gradient-to-r from-[#1a1f2e] to-[#111827] text-white shadow-lg border border-[#1e2d47]' : 'text-gray-500'}`}>
              <i className={`ri-moon-fill ${isDark ? 'text-[#00d4ff]' : ''}`}></i>
              <span>{t('profile.theme.dark')}</span>
              {isDark && <span className="w-2 h-2 rounded-full bg-[#00d4ff] animate-pulse"></span>}
            </button>
            <button onClick={() => setTheme('light')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap ${!isDark ? 'bg-white text-gray-900 shadow-lg border border-gray-200' : 'text-gray-600'}`}>
              <i className={`ri-sun-fill ${!isDark ? 'text-amber-500' : ''}`}></i>
              <span>{t('profile.theme.light')}</span>
              {!isDark && <span className="w-2 h-2 rounded-full bg-amber-500"></span>}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div onClick={() => setTheme('dark')} className={`p-3 rounded-xl border cursor-pointer transition-all ${isDark ? 'border-[#00d4ff]/40' : 'border-gray-200'} bg-[#08090F]`}>
              <div className="flex items-center gap-1.5 mb-2"><div className="w-2.5 h-2.5 rounded-full bg-[#00d4ff]"></div><div className="w-2.5 h-2.5 rounded-full bg-[#a78bfa]"></div></div>
              <div className="space-y-1"><div className="h-1.5 w-3/4 rounded bg-[#1e2d47]"></div><div className="h-1.5 w-1/2 rounded bg-[#00d4ff]/40"></div></div>
              <p className="text-[9px] text-[#00d4ff] font-bold mt-2">{t('profile.theme.dark')}</p>
            </div>
            <div onClick={() => setTheme('light')} className={`p-3 rounded-xl border cursor-pointer transition-all ${!isDark ? 'border-[#0070E0]/40' : 'border-gray-700'} bg-white`}>
              <div className="flex items-center gap-1.5 mb-2"><div className="w-2.5 h-2.5 rounded-full bg-[#0070E0]"></div><div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div></div>
              <div className="space-y-1"><div className="h-1.5 w-3/4 rounded bg-gray-200"></div><div className="h-1.5 w-1/2 rounded bg-[#0070E0]/40"></div></div>
              <p className="text-[9px] text-[#0070E0] font-bold mt-2">{t('profile.theme.light')}</p>
            </div>
          </div>
        </div>

        {/* Industry */}
        <div className={`p-4 rounded-2xl border ${c.bgCard} ${c.border}`}>
          <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>
            <i className="ri-building-line mr-1"></i>{t('profile.industry')}
            <span className={`ml-2 text-[10px] normal-case font-normal ${c.textMuted}`}>{selectedIndustries.length} {t('profile.industry.selected')}</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {INDUSTRIES.map(ind => (
              <button key={ind} onClick={() => toggleIndustry(ind)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${selectedIndustries.includes(ind) ? isDark ? 'bg-[#00d4ff]/20 text-[#00d4ff] border-[#00d4ff]/40' : 'bg-blue-100 text-blue-700 border-blue-200' : `${c.bgCard} ${c.textMuted} ${c.border}`}`}>
                {selectedIndustries.includes(ind) && <i className="ri-check-line mr-1"></i>}{ind}
              </button>
            ))}
          </div>
        </div>

        {/* Interest Tags */}
        <div className={`p-4 rounded-2xl border ${c.bgCard} ${c.border}`}>
          <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>
            <i className="ri-price-tag-3-line mr-1"></i>{t('profile.interests')}
          </p>
          <div className="flex flex-wrap gap-2">
            {INTEREST_TAGS.map(tag => (
              <button key={tag} onClick={() => toggleTag(tag)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${selectedTags.includes(tag) ? isDark ? 'bg-[#a78bfa]/20 text-[#a78bfa] border-[#a78bfa]/40' : 'bg-purple-100 text-purple-700 border-purple-200' : `${c.bgCard} ${c.textMuted} ${c.border}`}`}>
                {selectedTags.includes(tag) && <i className="ri-check-line mr-1"></i>}{tag}
              </button>
            ))}
          </div>
        </div>

        {/* Notifications */}
        <div className={`p-4 rounded-2xl border ${c.bgCard} ${c.border}`}>
          <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>
            <i className="ri-notification-3-line mr-1"></i>{t('profile.notifications')}
          </p>
          <div className="flex flex-col gap-3">
            {notifItems.map(item => (
              <div key={item.key} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${notifications[item.key] ? (isDark ? 'bg-[#00d4ff]/15 text-[#00d4ff]' : 'bg-blue-50 text-blue-600') : (isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400')}`}>
                    <i className={`${item.icon} text-sm`}></i>
                  </div>
                  <div>
                    <p className={`text-xs font-bold ${c.text}`}>{item.label}</p>
                    <p className={`text-[10px] ${c.textMuted}`}>{item.desc}</p>
                  </div>
                </div>
                <button onClick={() => toggleNotif(item.key)} className={`relative w-10 h-6 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap flex-shrink-0 ${notifications[item.key] ? (isDark ? 'bg-[#00d4ff]' : 'bg-[#0070E0]') : (isDark ? 'bg-gray-700' : 'bg-gray-200')}`}>
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all duration-300 ${notifications[item.key] ? 'left-5' : 'left-1'}`} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Menu */}
        <div className={`rounded-2xl border overflow-hidden ${c.bgCard} ${c.border}`}>
          {menuItems.map((item, i, arr) => (
            <button key={item.label} className={`w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-all ${c.bgCardHover} ${i < arr.length - 1 ? `border-b ${c.border}` : ''}`}>
              <i className={`${item.icon} text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}></i>
              <div className="flex-1 text-left">
                <p className={`text-sm font-semibold ${c.text}`}>{item.label}</p>
                <p className={`text-[10px] ${c.textMuted}`}>{item.sub}</p>
              </div>
              <i className={`ri-arrow-right-s-line ${c.textMuted}`}></i>
            </button>
          ))}
        </div>

        <button className={`w-full py-3.5 rounded-2xl text-sm font-bold text-rose-400 border border-rose-500/30 cursor-pointer whitespace-nowrap transition-all ${isDark ? 'bg-rose-500/10 hover:bg-rose-500/20' : 'bg-rose-50 hover:bg-rose-100'}`}>
          <i className="ri-logout-box-line mr-2"></i>{t('profile.signout')}
        </button>
        <p className={`text-center text-[10px] ${c.textMuted} pb-2`}>{t('profile.footer')}</p>
      </div>
    </div>
  );
};

export default ProfileScreen;
